'use client';

import { useCallback } from 'react';
import { getUserAccountPublicKeySync } from '@velocity-exchange/sdk';
import invariant from 'tiny-invariant';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import type { Adapter } from '@solana/wallet-adapter-base';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { updateUserNameIx } from '@velocity-exchange/common';

/**
 * Hook for renaming a subaccount.
 */
export const useRenameSubaccount = () => {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const renameSubaccountAction = useCallback(
		async (wallet: UiEnhancedIWalletV2, accountId: number, newName: string) => {
			invariant(velocityClient, 'driftClient is required');
			const userAccountPublicKey = getUserAccountPublicKeySync(
				velocityClient.program.programId,
				velocityClient.wallet.publicKey,
				accountId
			);
			const ix = await updateUserNameIx({
				velocityClient: velocityClient,
				newName,
				userAccountPublicKey,
				subAccountId: accountId,
			});
			await wallet.signAndSendTransaction(ix, getTxParams());
		},
		[velocityClient, getTxParams]
	);

	const wrappedRenameSubaccount = useSponsoredSignVelocityActionWithFallback(
		renameSubaccountAction
	);

	const renameSubaccount = useCallback(
		async (accountId: number, newName: string): Promise<boolean> => {
			if (!velocityClient) return false;

			const toastId = `renamingSubaccount${accountId}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedRenameSubaccount(accountId, newName);

				notify({
					id: toastId,
					type: 'success',
					message: 'Account name updated.',
					showUntilCancelled: false,
				});

				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as Adapter,
				});
				return false;
			}
		},
		[velocityClient, walletAdapter, wrappedRenameSubaccount]
	);

	return { renameSubaccount };
};
